import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Button,
  HStack,
  Spinner,
  VStack,
  Text,
  Input,
  ErrorBanner,
} from 'ui_2';
import { useSquash, useSquashTime, useSquashMint } from '@src/hooks/useSquash';
import introSquash from '../../../public/assets/introSquash.png';
import { getRecords } from '@puzzlehq/sdk';
import { RecordStatus } from '@puzzlehq/types';
import { Route as GameIndexRoute } from './index.js';
import { SelectedAccount } from '@src/components/SelectedAccount.js';
import { LoadingMessage } from '@src/components/LoadingMessage.js';
import { useEffect, useState } from 'react';
import z from 'zod';
import { IncorrectNetworkModal } from '@src/components/IncorrectNetworkDialog.js';
import { MdOutlineTimer } from 'react-icons/md';
import { FeeViewBanner } from '@src/components/FeeBannerView.js';
import { SunRising } from '@src/components/SunRising.js';
import { useArcadeCoins } from '@puzzlehq/frontend-utils';
import { MuteButton } from '@src/components/MuteButton.js';
import { PARCBalance } from '@src/components/PARCBalance.js';
import { MintTextMarquee } from '@src/components/MintTextMarquee.js';
import useWindowDimensions from '@src/hooks/useWindowDimensions';
import { extractAmountFromInputString } from 'jigsaw';
import { useIntroAudio } from '@src/hooks/useAudio.js';
import { useFeeValid } from '@src/hooks/useFeeValid.js';
import { SquashHistoryButton } from '@src/components/SquashHistoryButton.js';
import { useSquashGameState } from '@src/providers/squashGameState.js';
import debug from 'debug';

const log_mint = debug('route:mint');
log_mint.enabled = true;

const mintSearchSchema = z.object({
  mintEventId: z.string().optional(),
  expectedRecordCount: z.number().int().positive().optional(),
  finishingGame: z.boolean().optional(),
});

export const Route = createFileRoute('/game/mint')({
  validateSearch: mintSearchSchema,
  beforeLoad: async () => {
    const _squashRecords = await getRecords({
      filter: {
        status: 'All',
        programIds: [import.meta.env.VITE_SQUASH_PROGRAM_ID],
      },
    });
    const squashMintRecords = _squashRecords.records.filter(
      (r) =>
        ['mint', 'migrate_from_v3', 'mint_arbitrary_squash'].includes(
          r.functionId
        ) && [RecordStatus.Unspent, RecordStatus.Pending].includes(r.status)
    );

    log_mint('squashMintRecords', squashMintRecords);

    const activeSquashMintRecord = squashMintRecords?.find((record) => {
      const level = extractAmountFromInputString(record?.data?.level);
      return level !== null && level < 5;
    });
    log_mint('activeSquashMintRecord', activeSquashMintRecord);

    if (activeSquashMintRecord) {
      // you're not supposed to be here...
      log_mint('squashMintRecord is defined already!');
      throw redirect({
        to: GameIndexRoute.to,
      });
    }
  },
  pendingComponent: () => <LoadingMessage message='Loading your game' />,
  component: function Component() {
    const { arcadeCoin, balance: parcBalance } = useArcadeCoins();
    const [name, setName] = useState('');
    const isNameValid = name.length > 0 && name.length <= 16;
    const { account, activeSquash, setGameOver } = useSquashGameState();
    const { gameIsActive } = useSquashTime();
    const { timeRemaining, timeRemainingStr } = useSquash();
    const { readyToMint, isMinting, cachedName, error, mint } = useSquashMint(
      name,
      arcadeCoin
    );
    const navigate = useNavigate({ from: Route.id });
    const { width } = useWindowDimensions();
    const { finishingGame } = Route.useSearch();

    useEffect(() => {
      if (finishingGame) {
        setGameOver?.(true);
        navigate({
          to: Route.to,
          search: (prev) => ({
            ...prev,
            finishingGame: undefined,
          }),
        });
      }
    }, [finishingGame]);

    useEffect(() => {
      if (cachedName && cachedName !== '' && name && name !== '') {
        setName(cachedName);
      }
    }, [name, cachedName]);

    const feeValid = useFeeValid('mint');

    useEffect(() => {
      if (activeSquash && account) {
        navigate({
          to: GameIndexRoute.to,
        });
      }
    }, [activeSquash, account]);

    useIntroAudio();

    return (
      <VStack className='relative h-[100dvh] flex-none gap-4 bg-squashBg p-4 font-body'>
        <HStack className='w-full'>
          <VStack className='w-full items-start gap-2'>
            <PARCBalance balance={parcBalance} parcRecord={arcadeCoin} />
            {parcBalance === 0 && <ErrorBanner text='Low Parc balance!' />}
          </VStack>
          <VStack className='flex-grow items-end gap-2'>
            {account && <SelectedAccount account={account} />}
            <HStack className='items-center gap-2'>
              <SquashHistoryButton />
              <MuteButton />
            </HStack>
          </VStack>
        </HStack>

        <VStack className='flex-grow items-center justify-center gap-4'>
          <VStack className='items-center gap-2'>
            <div className='max-w-[400px] text-center'>
              <Text font={width < 400 ? 'bold-md' : 'bold-lg'} family='atak'>
                Wow! You got a Squash Baby.
                <br />
                Give it a name.
              </Text>
            </div>
            <img
              className='w-auto max-w-[200px] justify-center object-contain'
              src={introSquash}
            />
          </VStack>
          <HStack className='w-full max-w-[80%] justify-center'>
            <Input
              autoCorrect='off'
              autoComplete='off'
              className=' self-center'
              placeholder='Name your Squash'
              value={name}
              disabled={isMinting}
              error={
                !isNameValid && name.length > 0
                  ? 'Name must be 16 characters or less'
                  : undefined
              }
              onChange={(e) => setName(e.target.value)}
            />
          </HStack>
          <MintTextMarquee active={isMinting} />
          {feeValid === false && (
            <VStack className='rounded-[10px] bg-gray-50 px-2 py-1'>
              <Text>Low Aleo balance!</Text>
            </VStack>
          )}
          {error && (
            <VStack className='rounded-[10px] bg-red-50 px-2 py-1'>
              <Text color='red'>{error}</Text>
            </VStack>
          )}
        </VStack>

        {/* Bottom section with mint button */}
        <VStack className='mb-10 w-full items-center gap-2'>
          {gameIsActive && (
            <VStack className='w-full items-center gap-2'>
              <Button
                className='h-[4rem] gap-2 font-body text-[24px] font-normal text-black'
                disabled={
                  !readyToMint || isMinting || !isNameValid || !feeValid
                }
                onClick={mint}
                variant='squashMint'
              >
                <VStack className='items-center justify-center'>
                  <HStack>
                    <span role='img' className='mr-2'>
                      {String.fromCodePoint(0x1f331)}
                    </span>
                    {isMinting ? 'Minting' : 'Mint'}
                  </HStack>
                  <Text font='light-sm' color='gray-600'>
                    Cost: 1 PARC
                  </Text>
                </VStack>
                {isMinting && <Spinner />}
              </Button>
              {timeRemaining && timeRemaining < 60_000 * 5 && (
                <HStack className='items-center justify-center gap-2'>
                  <MdOutlineTimer className='text-sm text-gray-600' />
                  <Text font='light-sm' color='gray-600'>
                    {timeRemainingStr}
                  </Text>
                </HStack>
              )}
            </VStack>
          )}
        </VStack>

        <IncorrectNetworkModal />
        <div className='fixed bottom-0 left-0 right-0 z-30 flex justify-center'>
          <FeeViewBanner functionId='mint' />
        </div>
        {gameIsActive === false && <SunRising />}
      </VStack>
    );
  },
});
